import React, { useState, useEffect } from 'react';
import { Paper, Chip, Button, IconButton, CircularProgress, Typography, Box } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import AssignmentOutlinedIcon from '@material-ui/icons/AssignmentOutlined';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import GavelOutlinedIcon from '@material-ui/icons/GavelOutlined';
import ForumOutlinedIcon from '@material-ui/icons/ForumOutlined';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import { EmptyCollection, DeletePopUp } from '../../../../shared_elements';
import TransitionFormDialog from './TransitionFormDialog';
import TransitionGuardFormDialog from './TransitionGuardFormDialog';
import TransitionActionFormDialog from './TransitionActionFormDialog';
import { GUARD_TYPE_OPTIONS, ACTION_TYPE_OPTIONS } from '../constants';
import { TEXT, SURFACE } from '../../shared_components';
import { checkApiStatus } from '../../../../utils_v2';
import { trackActivity } from '../../../../utils/mixpanel';
import { globalDeleteService } from '../../../../utils_v2/globalApiServices';
import { deleteTransitionApi, deleteTransitionGuardApi, deleteTransitionActionApi } from '../apiService';

const idOf = (x) => (x && typeof x === 'object' ? x.id : x);
const labelOf = (x) => (x && typeof x === 'object' ? x.label : x);

const guardTypeLabel = (type) => (GUARD_TYPE_OPTIONS.find((o) => o.id === idOf(type)) || {}).label || labelOf(type);
const actionTypeLabel = (type) => (ACTION_TYPE_OPTIONS.find((o) => o.id === idOf(type)) || {}).label || labelOf(type);

const guardSummary = (guard) => {
  const type = idOf(guard?.guard_type);
  if (type === 'field_check') return `${labelOf(guard?.field_name) || ''} ${labelOf(guard?.operator) || ''} ${guard?.value || ''}`.trim();
  if (type === 'asset_count') return `Asset Count >= ${guard?.min_count}`;
  return guard?.error_message || '';
};

const actionSummary = (action) => {
  const type = idOf(action?.action_type);
  if (type === 'send_notification') return `To: ${labelOf(action?.config?.recipient) || ''}${action?.config?.message ? ` — ${action.config.message}` : ''}`;
  if (type === 'set_field') return `${labelOf(action?.config?.field) || ''} = ${action?.config?.value ?? ''}`;
  return '';
};

/* guard_type-driven icon tile, mirrors the accent used across the Stages tab cards. */
const GUARD_TYPE_STYLE = {
  field_check: { icon: AssignmentOutlinedIcon, color: '#6d28d9', background: '#f2eaff' },
  asset_count: { icon: CheckCircleOutlineIcon, color: '#16a34a', background: '#eaf8ee' },
  rep_assigned: { icon: PeopleOutlineIcon, color: '#7c3aed', background: '#f2eaff' },
  lead_assigned: { icon: PeopleOutlineIcon, color: '#7c3aed', background: '#f2eaff' },
  confirm: { icon: ForumOutlinedIcon, color: '#2879e8', background: '#eaf2ff' },
};
const DEFAULT_GUARD_STYLE = { icon: GavelOutlinedIcon, color: '#5426ff', background: '#f0eaff' };

const TransitionList = ({ transitions, selectedId, onSelect }) => {
  const list = transitions || [];
  return (
    <Paper elevation={0} style={{ width: 300, flexShrink: 0, border: `1px solid ${SURFACE.border}`, borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ padding: '14px 14px 12px' }}>
        <Typography style={{ fontSize: 15, fontWeight: 700, color: TEXT.primary }}>
          TRANSITIONS ({list.length})
        </Typography>
      </div>

      <div style={{ borderTop: `1px solid ${SURFACE.divider}` }}>
        {list.length ? list.map((t) => {
          const selected = t?.id === selectedId;
          return (
            <Box
              key={t?.id}
              onClick={() => onSelect(t?.id)}
              style={{
                display: 'flex', alignItems: 'center', padding: '14px 16px', cursor: 'pointer',
                borderLeft: `3px solid ${selected ? '#5426ff' : 'transparent'}`,
                backgroundColor: selected ? '#f3f1ff' : 'transparent',
                borderBottom: `1px solid ${SURFACE.divider}`,
              }}
            >
              <Box
                style={{
                  width: 16, height: 16, borderRadius: '50%', flexShrink: 0, marginRight: 14,
                  border: `2px solid ${selected ? '#5426ff' : '#c3c9d6'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {selected ? <Box style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#5426ff' }} /> : null}
              </Box>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Typography style={{ fontSize: 13, fontWeight: selected ? 700 : 600, color: TEXT.primary }} noWrap>
                  {t?.from_stage?.name} <span style={{ color: TEXT.tertiary }}>→</span> {t?.to_stage?.name}
                </Typography>
                <Typography style={{ fontSize: 12, color: TEXT.tertiary, marginTop: 3 }}>
                  {(t?.guards || []).length} Guards • {(t?.actions || []).length} Actions
                </Typography>
              </Box>
              {selected ? <ArrowForwardIcon style={{ fontSize: 18, color: '#5426ff', flexShrink: 0 }} /> : null}
            </Box>
          );
        }) : null}
      </div>
    </Paper>
  );
};

const RoleBadge = ({ role }) => (
  <Chip size="small" label={role} style={{ marginRight: 6, backgroundColor: '#3fb9501f', color: '#3fb950', fontWeight: 600 }} />
);

const SectionLabel = ({ children, count, action }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '20px 0 12px' }}>
    <Typography style={{ fontSize: 13, fontWeight: 700, color: '#5426ff', letterSpacing: 0.3 }}>
      {children.toUpperCase()}{count !== undefined ? ` (${count})` : ''}
    </Typography>
    {action}
  </div>
);

const RowActions = ({ onEdit, onDelete }) => (
  <Box style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
    <IconButton size="small" onClick={() => onEdit()}>
      <EditOutlinedIcon style={{ color: TEXT.tertiary, fontSize: 18 }} />
    </IconButton>
    <IconButton size="small" onClick={() => onDelete()}>
      <DeleteOutlineIcon style={{ color: '#f85149', fontSize: 18 }} />
    </IconButton>
  </Box>
);

const GuardCard = ({ guard, onEdit, onDelete }) => {
  const style = GUARD_TYPE_STYLE[idOf(guard?.guard_type)] || DEFAULT_GUARD_STYLE;
  const Icon = style.icon;
  return (
    <Box style={{ display: 'flex', alignItems: 'center', minHeight: 66, padding: '8px 12px', border: `1px solid ${SURFACE.border}`, borderRadius: 7, marginBottom: 8, backgroundColor: '#fff' }}>
      <Box style={{ width: 44, height: 44, borderRadius: 10, backgroundColor: style.background, display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 14, flexShrink: 0 }}>
        <Icon style={{ color: style.color, fontSize: 24 }} />
      </Box>
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Typography style={{ fontSize: 14, fontWeight: 700, color: TEXT.primary }}>{guard?.name || guardTypeLabel(guard?.guard_type)}</Typography>
        <Typography style={{ fontSize: 12, color: TEXT.tertiary, marginTop: 2 }}>{guardSummary(guard)}</Typography>
      </Box>
      <RowActions onEdit={onEdit} onDelete={onDelete} />
    </Box>
  );
};

const ActionCard = ({ action, index, onEdit, onDelete }) => (
  <Box style={{ display: 'flex', alignItems: 'center', minHeight: 58, padding: '8px 12px', border: `1px solid ${SURFACE.border}`, borderRadius: 7, marginBottom: 8, backgroundColor: '#fff' }}>
    <Box style={{
      width: 36, height: 36, borderRadius: '50%', backgroundColor: '#f0eaff', color: '#5426ff',
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, marginRight: 14, flexShrink: 0,
    }}
    >
      {index + 1}
    </Box>
    <Box style={{ flex: 1, minWidth: 0 }}>
      <Typography style={{ fontSize: 14, fontWeight: 700, color: TEXT.primary }}>{actionTypeLabel(action?.action_type)}</Typography>
      <Typography style={{ fontSize: 12, color: TEXT.tertiary, marginTop: 2 }}>{actionSummary(action)}</Typography>
    </Box>
    <Chip label={`Step ${action?.order}`} size="small" style={{ backgroundColor: '#f1f3f7', color: '#425175', fontSize: 12, marginRight: 4 }} />
    <RowActions onEdit={onEdit} onDelete={onDelete} />
  </Box>
);

const TransitionDetail = ({
  transition, onAddGuard, onAddAction, onEditTransition, onDeleteTransition,
  onEditGuard, onDeleteGuard, onEditAction, onDeleteAction,
}) => (
  <Paper elevation={0} style={{ flex: 1, minWidth: 0, border: `1px solid ${SURFACE.border}`, borderRadius: 8, padding: '20px 22px' }}>
    <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
      <Box style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <Typography style={{ fontSize: 19, fontWeight: 700, color: TEXT.primary }}>{transition?.from_stage?.name}</Typography>
        <ArrowForwardIcon style={{ color: TEXT.tertiary, fontSize: 20 }} />
        <Typography style={{ fontSize: 19, fontWeight: 700, color: TEXT.primary }}>{transition?.to_stage?.name}</Typography>
        {transition?.label ? <Chip size="small" label={transition.label} style={{ marginLeft: 8, backgroundColor: '#eef0f4', color: '#52617e' }} /> : null}
      </Box>
      <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <IconButton size="small" onClick={() => onEditTransition()} style={{ border: `1px solid ${SURFACE.border}`, borderRadius: 6 }}>
          <EditOutlinedIcon style={{ fontSize: 19, color: '#394a6a' }} />
        </IconButton>
        <IconButton size="small" onClick={() => onDeleteTransition()} style={{ border: `1px solid ${SURFACE.border}`, borderRadius: 6 }}>
          <DeleteOutlineIcon style={{ fontSize: 19, color: '#f85149' }} />
        </IconButton>
      </Box>
    </Box>

    <SectionLabel count={(transition?.guards || []).length} action={<Button variant="outlined" size="small" color="primary" startIcon={<AddIcon />} onClick={() => onAddGuard()}>Add Guard</Button>}>
      🛡 Guards
    </SectionLabel>
    {(transition?.guards || []).length ? transition.guards.map((guard, index) => (
      <GuardCard key={guard?.id || index} guard={guard} onEdit={() => onEditGuard(guard)} onDelete={() => onDeleteGuard(guard)} />
    )) : <EmptyCollection title="No guards configured" />}

    <SectionLabel count={(transition?.actions || []).length} action={<Button variant="outlined" size="small" color="primary" startIcon={<AddIcon />} onClick={() => onAddAction()}>Add Action</Button>}>
      ⚡ Actions
    </SectionLabel>
    {(transition?.actions || []).length ? [...transition.actions].sort((a, b) => a?.order - b?.order).map((action, index) => (
      <ActionCard key={action?.id || index} action={action} index={index} onEdit={() => onEditAction(action)} onDelete={() => onDeleteAction(action)} />
    )) : <EmptyCollection title="No actions configured" />}

    <SectionLabel>👤 Allowed Roles</SectionLabel>
    <div>
      {(transition?.allowed_roles || []).length ? transition.allowed_roles.map((role) => <RoleBadge key={role} role={role} />)
        : <span style={{ fontSize: 12, color: TEXT.tertiary }}>Any role</span>}
    </div>

    <SectionLabel>💬 Confirmation</SectionLabel>
    {transition?.requires_confirmation ? (
      <div style={{ fontSize: 13, fontStyle: 'italic', color: '#4b5563', padding: 12, backgroundColor: '#fff7ed', borderRadius: 6 }}>
        &quot;{transition?.confirm_message}&quot;
      </div>
    ) : <span style={{ fontSize: 12, color: TEXT.tertiary }}>Not required</span>}
  </Paper>
);

const transitionDeleteErrorMessage = (response) => response?.data?.message || 'Unable to delete the transition due to a technical error.';
const guardDeleteErrorMessage = (response) => response?.data?.message || 'Unable to delete the guard due to a technical error.';
const actionDeleteErrorMessage = (response) => response?.data?.message || 'Unable to delete the action due to a technical error.';

const TransitionsTab = ({ templateId, stages, transitions, loading, onTransitionsChanged, enqueueSnackbar, showAddTransition, onCloseAddTransition }) => {
  const [selectedId, setSelectedId] = useState(null);
  const [addEditTransition, setAddEditTransition] = useState({ modal: false, mode: '', data: null });
  const [addEditGuard, setAddEditGuard] = useState({ modal: false, mode: '', data: null });
  const [addEditAction, setAddEditAction] = useState({ modal: false, mode: '', data: null });
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'transition' | 'guard' | 'action', item }
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setSelectedId((prev) => (prev && (transitions || []).some((t) => t?.id === prev) ? prev : ((transitions || [])[0] || {})?.id || null));
  }, [transitions]);

  useEffect(() => {
    if (showAddTransition) setAddEditTransition({ modal: true, mode: 'create', data: null });
  }, [showAddTransition]);

  const closeAddEditTransition = () => {
    setAddEditTransition({ modal: false, mode: '', data: null });
    onCloseAddTransition();
  };

  if (!templateId) return <EmptyCollection title="Select a template to view its transitions" />;
  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><CircularProgress size={24} /></div>;

  const selected = (transitions || []).find((t) => t?.id === selectedId);

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const { type, item } = deleteTarget;
    const request = type === 'transition' ? globalDeleteService(deleteTransitionApi(item?.id))
      : type === 'guard' ? globalDeleteService(deleteTransitionGuardApi(item?.id))
        : globalDeleteService(deleteTransitionActionApi(item?.id));
    const errorMessage = type === 'transition' ? transitionDeleteErrorMessage : type === 'guard' ? guardDeleteErrorMessage : actionDeleteErrorMessage;
    const successMessage = type === 'transition' ? 'Transition deleted' : type === 'guard' ? 'Guard deleted' : 'Action deleted';
    const entityLabel = type === 'transition' ? 'Transition' : type === 'guard' ? 'Transition Guard' : 'Transition Action';

    request.then((response) => {
      setDeleting(false);
      if (checkApiStatus(response)) {
        enqueueSnackbar(successMessage, { variant: 'success' });
        setDeleteTarget(null);
        onTransitionsChanged();
        trackActivity('Project Management', {
          event_type: `Delete ${entityLabel} Success`,
          page_title: 'Planner Tracker Transition Rules',
          template_id: templateId || '',
          [`${type}_id`]: item?.id || '',
          response_msg: response?.data?.message,
        });
      } else {
        enqueueSnackbar(errorMessage(response), { variant: 'error' });
        trackActivity('Project Management', {
          event_type: `Delete ${entityLabel} Failed`,
          page_title: 'Planner Tracker Transition Rules',
          template_id: templateId || '',
          [`${type}_id`]: item?.id || '',
          error_source: 'Backend',
          error_message: errorMessage(response),
        });
      }
    });
  };

  const deleteContent = () => {
    if (!deleteTarget) return null;
    const { type, item } = deleteTarget;
    if (type === 'transition') return <h4>Are you sure you want to delete the transition &quot;{item?.from_stage?.name} → {item?.to_stage?.name}&quot;?</h4>;
    if (type === 'guard') return <h4>Are you sure you want to delete this guard?</h4>;
    return <h4>Are you sure you want to delete this action?</h4>;
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        {(transitions || []).length ? (
          <>
            <TransitionList transitions={transitions} selectedId={selectedId} onSelect={setSelectedId} />
            {selected ? (
              <TransitionDetail
                transition={selected}
                onAddGuard={() => setAddEditGuard({ modal: true, mode: 'create', data: null })}
                onAddAction={() => setAddEditAction({ modal: true, mode: 'create', data: null })}
                onEditTransition={() => setAddEditTransition({ modal: true, mode: 'edit', data: selected })}
                onDeleteTransition={() => setDeleteTarget({ type: 'transition', item: selected })}
                onEditGuard={(guard) => setAddEditGuard({ modal: true, mode: 'edit', data: guard })}
                onDeleteGuard={(guard) => setDeleteTarget({ type: 'guard', item: guard })}
                onEditAction={(action) => setAddEditAction({ modal: true, mode: 'edit', data: action })}
                onDeleteAction={(action) => setDeleteTarget({ type: 'action', item: action })}
              />
            ) : null}
          </>
        ) : (
          <EmptyCollection title="No transitions configured for this template yet" />
        )}
      </div>

      {addEditTransition?.modal ? (
        <TransitionFormDialog
          stages={stages}
          transitions={transitions}
          addEditTransition={addEditTransition}
          onToggleModal={closeAddEditTransition}
          getResponseBack={() => { closeAddEditTransition(); onTransitionsChanged(); }}
          enqueueSnackbar={enqueueSnackbar}
        />
      ) : null}

      {addEditGuard?.modal && selected ? (
        <TransitionGuardFormDialog
          addEditGuard={addEditGuard}
          transitionId={selected?.id}
          onToggleModal={() => setAddEditGuard({ modal: false, mode: '', data: null })}
          getResponseBack={() => { setAddEditGuard({ modal: false, mode: '', data: null }); onTransitionsChanged(); }}
          enqueueSnackbar={enqueueSnackbar}
        />
      ) : null}

      {addEditAction?.modal && selected ? (
        <TransitionActionFormDialog
          addEditAction={addEditAction}
          transitionId={selected?.id}
          nextOrder={(selected?.actions || []).length + 1}
          onToggleModal={() => setAddEditAction({ modal: false, mode: '', data: null })}
          getResponseBack={() => { setAddEditAction({ modal: false, mode: '', data: null }); onTransitionsChanged(); }}
          enqueueSnackbar={enqueueSnackbar}
        />
      ) : null}

      {deleteTarget ? (
        <DeletePopUp
          modal
          title={`Delete ${deleteTarget.type === 'transition' ? 'Transition' : deleteTarget.type === 'guard' ? 'Guard' : 'Action'}`}
          content={deleteContent()}
          toggleModalFn={() => setDeleteTarget(null)}
          deleteRecordFn={handleConfirmDelete}
          circularloader={deleting}
        />
      ) : null}
    </div>
  );
};

export default TransitionsTab;
